import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plane, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { PTO_TYPE_LABELS, STATUS_STYLES, formatDateShort } from './hrUtils';

export const TimeOffTab = () => {
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'vacation', start_date: '', end_date: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    Promise.all([hrAPI.myPtoBalance(), hrAPI.listPtoRequests()])
      .then(([b, r]) => { setBalance(b.data); setRequests(r.data); })
      .catch(() => toast.error('Failed to load time off data'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error('Select start and end dates'); return; }
    setSubmitting(true);
    try {
      await hrAPI.createPtoRequest(form);
      toast.success('Time off request submitted');
      setShowForm(false);
      setForm({ type: 'vacation', start_date: '', end_date: '', reason: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    try {
      await hrAPI.cancelPtoRequest(id);
      toast.success('Request cancelled');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (!balance || !requests) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6" data-testid="hr-timeoff-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { key: 'vacation', label: 'Vacation Days', color: 'text-cyan-600 bg-cyan-100' },
          { key: 'sick', label: 'Sick Days', color: 'text-amber-600 bg-amber-100' },
          { key: 'personal', label: 'Personal Days', color: 'text-teal-600 bg-teal-100' },
        ].map((b) => (
          <Card key={b.key} className="bg-white border-slate-200">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{b.label}</p>
                <p className="text-3xl font-bold text-slate-800" data-testid={`hr-balance-${b.key}`}>{balance[b.key] ?? 0}</p>
                <p className="text-xs text-slate-400">days remaining</p>
              </div>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${b.color}`}>
                <Plane className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-slate-800 text-lg">My Time Off Requests</CardTitle>
          <Button onClick={() => setShowForm(!showForm)} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" size="sm" data-testid="hr-new-pto-btn">
            <Plus className="w-4 h-4 mr-1" /> Request Time Off
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showForm && (
            <div className="p-4 rounded-xl border border-cyan-200 bg-cyan-50/50 space-y-3" data-testid="hr-pto-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-700">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="hr-pto-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PTO_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-white border-slate-200 mt-1" data-testid="hr-pto-start-input" />
                </div>
                <div>
                  <Label className="text-slate-700">End Date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-white border-slate-200 mt-1" data-testid="hr-pto-end-input" />
                </div>
              </div>
              <div>
                <Label className="text-slate-700">Reason (optional)</Label>
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-white border-slate-200 mt-1" rows={2} data-testid="hr-pto-reason-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={submit} disabled={submitting} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="hr-pto-submit-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit Request
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)} className="rounded-full">Cancel</Button>
              </div>
              <p className="text-xs text-slate-400">Business days (Mon–Fri) are counted automatically. Approved requests deduct from your balance.</p>
            </div>
          )}

          <div className="space-y-2" data-testid="hr-pto-requests-list">
            {requests.length === 0 && <p className="text-sm text-slate-400 py-4">No time off requests yet.</p>}
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800">{PTO_TYPE_LABELS[r.type] || r.type}</p>
                    <Badge className={STATUS_STYLES[r.status] || ''} data-testid={`hr-pto-status-${r.id}`}>{r.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    {formatDateShort(r.start_date)} → {formatDateShort(r.end_date)} · {r.days} business day{r.days === 1 ? '' : 's'}
                    {r.reason ? ` · ${r.reason}` : ''}
                  </p>
                  {r.review_note && <p className="text-xs text-slate-400 mt-0.5">Note from HR: {r.review_note}</p>}
                </div>
                {r.status === 'pending' && (
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 rounded-full shrink-0" onClick={() => cancel(r.id)} data-testid={`hr-pto-cancel-${r.id}`}>
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
